import { useGetMasterListByUrlQuery } from 'app/services/ahuComponentFormsApi';
import { COMPONENT_FORM_CONFIGS } from '../componentFormConfigs';
import { useComponentForm } from '../useComponentForm';
import { FormSubtab, handleTabToNext, type OptionRow } from '../formKit';
import { deriveSeasonValue } from '../cmsDoc';
import { ComponentSeasonBlock } from '../SeasonGroup';
import {
  AirStreamFlow,
  BlankOffSlot,
  FormHeading,
  MBareInput,
  MBareSelect,
  MSelect,
  PSelect,
  RequiredFieldNote,
} from './formCommon';
import type { ComponentFormProps } from './ComponentFormHost';

/**
 * Phase 6e2a — SILENCER form (§50.1 row; legacy form.js 191 + form.tmpl 446;
 * parity 322): widths/heights/lengths additionalLoadables from
 * master/v1.0.0/ahu/silencers/{widths,heights,lengths}/ gated on mfg+series,
 * expanded client-side to integer lists (range_min..range_max);
 * supplyed_by [sic] model; auto-single-select ALWAYS armed (§49.3).
 * Rating mode: setupRatingMode with callOnDispatcher:false +
 * showCountElement (chassis stub).
 */
const config = COMPONENT_FORM_CONFIGS.silencer;

/** lengths rows → flat option list (legacy getLengths: model.length each). */
const lengthOptions = (rows: OptionRow[] | undefined): OptionRow[] =>
  (rows ?? [])
    .map((r) => Number(r.length))
    .filter((v, i, a) => Number.isFinite(v) && a.indexOf(v) === i)
    .map((v) => ({ id: v, value: v }));

/** range rows → integer option list (legacy client-side expansion). */
const expandRanges = (rows: OptionRow[] | undefined): OptionRow[] => {
  const out: OptionRow[] = [];
  const seen = new Set<number>();
  (rows ?? []).forEach((r) => {
    const min = Number(r.range_min ?? r.min ?? r.value);
    const max = Number(r.range_max ?? r.max ?? r.value);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    for (let v = Math.ceil(min); v <= max; v += 1) {
      if (!seen.has(v)) {
        seen.add(v);
        out.push({ id: v, value: v });
      }
    }
  });
  return out;
};

const SilencerForm = ({ row, section, section2, cms }: ComponentFormProps) => {
  const api = useComponentForm({
    config,
    row,
    section,
    section2,
    firstSubtab: 'properties',
  });
  const { model } = api;

  // widths/heights/lengths gated on mfg+series (skip until both set).
  const engine = api.catalogueFields.master_silencer_id;
  const mfgId = engine?.paramValue('mfg_id');
  const seriesId = engine?.paramValue('series_id');
  const gate = mfgId != null && seriesId != null;
  const rangeQuery = gate ? `?mfg_id=${mfgId}&series_id=${seriesId}` : '';
  const { data: widths } = useGetMasterListByUrlQuery(
    `/api/v1.0/master/v1.0.0/ahu/silencers/widths/${rangeQuery}`,
    { skip: !gate }
  );
  const { data: heights } = useGetMasterListByUrlQuery(
    `/api/v1.0/master/v1.0.0/ahu/silencers/heights/${rangeQuery}`,
    { skip: !gate }
  );
  const { data: lengths } = useGetMasterListByUrlQuery(
    `/api/v1.0/master/v1.0.0/ahu/silencers/lengths/${rangeQuery}`,
    { skip: !gate }
  );
  const { data: supplyedBy } = useGetMasterListByUrlQuery(
    '/api/v1.0/master/v1.0.0/ahu/supplyed_by/' // [sic] legacy route (verify 6e2a-6)
  );

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const sub = (name: string) => !!api.subtabs[name];
  const selectMode2 = Number(model.get('select_mode_id')) === 2;
  const octaves = ['31', '63', '125', '250', '500', '1000', '2000', '4000', '8000'];

  return (
    <>
      <FormHeading
        title="Silencer"
        api={api}
        wrapperClass="component-form-heading clearfix"
        chrome={[
          {
            field: config.catalogueFields[0],
            visClass: 'vis-silencer',
            searchResultClass: 'search-result',
          },
        ]}
      />
      <form onKeyDown={handleTabToNext}>
        <ul className="inputs-group max-height-auto">
          <FormSubtab type="silencer" cid={row.id} name="properties" label="Properties" labelHidden open={sub('properties')} onToggle={api.toggleSubtab}>
            {/* The trailing space in these labels is load-bearing: legacy puts
                the caption on its own line inside `<span class="label required">`
                (form.tmpl :43,:59,:75,:153,:172), so HTML renders a space before
                the `.required:after` asterisk (common.css :29077). JSX drops it,
                which is why the asterisks sit tight against the caption on dev3.
                `Manufacturer` is inline in the tmpl (:29) — no space there. */}
            <PSelect api={api} target="master_silencer_id" name="mfg_id" label="Manufacturer" required cmsPath="mfg" />
            <PSelect api={api} target="master_silencer_id" name="series_id" label={'Series '} required cmsPath="series" labelKey="series" />
            <PSelect api={api} target="master_silencer_id" name="type_id" label={'Type '} required cmsPath="type" />
            <PSelect api={api} target="master_silencer_id" name="shape_id" label={'Shape '} required cmsPath="shape" />
            {/* Likewise the `{' '}` between the controls below: form.tmpl :86-110
                and :146-152 have a newline between every select/input and its
                multiply-sign, and those spaces are part of the row's width — drop
                them and each field creeps left until the last one no longer lines
                up with the single-input rows above and below. */}
            <li>
              <div className="multiply silencer-multiply">
                {selectMode2 ? (
                  <>
                    <MBareSelect api={api} name="width" options={expandRanges(widths)} labelKey="value" valueKey="id" extraClass="small-input" />{' '}
                    <span className="multiply-sign">X</span>{' '}
                    <MBareSelect api={api} name="height" options={expandRanges(heights)} labelKey="value" valueKey="id" extraClass="small-input" />{' '}
                    <span className="multiply-sign">X</span>{' '}
                    <MBareSelect api={api} name="length" options={lengthOptions(lengths)} labelKey="value" valueKey="id" extraClass="small-input" />
                  </>
                ) : (
                  <>
                    <MBareInput api={api} name="width" extraClass="small-input" />{' '}
                    <span className="multiply-sign">X</span>{' '}
                    <MBareInput api={api} name="height" extraClass="small-input" />{' '}
                    <span className="multiply-sign">X</span>{' '}
                    <MBareInput api={api} name="length" extraClass="small-input" />
                  </>
                )}
                {/* QA 2026-09-02 — the three dimensions are required, but
                    form.tmpl :141 marks none of them. Deliberate deviation
                    from legacy (a required-label update, not parity): the
                    per-caption `.required` span is the board's own convention
                    for a multi-control row (dog_house form.tmpl :27-29,
                    DogHouseForm.tsx :95). */}
                <span className="label">
                  <span className="required">Width</span> x{' '}
                  <span className="required">Height</span> x{' '}
                  <span className="required">Length</span>
                </span>
              </div>
            </li>
            <li>
              <div className=" silencer-multiply">
                <MBareInput api={api} name="qty_wide" formatter="integer" extraClass="medium-input" fieldClass="model_field" />{' '}
                <span className="unit multiply-sign">X</span>{' '}
                <MBareInput api={api} name="qty_high" formatter="integer" extraClass="medium-input" fieldClass="model_field" />
                <span className="label required">
                  <span>Qty Wide</span> x <span>High</span>{' '}
                </span>
              </div>
            </li>
            <MSelect api={api} name="airflow_direction_id" label={'Airflow Direction '} required cmsPath="airflow_direction" hideNone />
            <PSelect api={api} target="master_silencer_id" name="transmission_loss_id" label="Transmission Loss" cmsPath="transmission_loss" />
            <PSelect api={api} target="master_silencer_id" name="outside_wall_id" label="Outside Wall Mtl" cmsPath="outside_wall" />
            <PSelect api={api} target="master_silencer_id" name="inside_liner_id" label="Inside Liner Mtl" cmsPath="inside_liner" />
            <PSelect api={api} target="master_silencer_id" name="media_fill_mtl_id" label="Media fill" cmsPath="media_fill_mtl" />
            <MSelect api={api} name="supplied_by_id" label={<span>Supplied By </span>} options={(supplyedBy ?? []) as OptionRow[]} extraClass=" " selectionClass="medium-selection" />
          </FormSubtab>

          {/* Dev-sync J6 — season split (silencer form.tmpl +442..514):
              data-season on the sublist ul; the season block appends after
              the octave rows. Silencer LDB|LWB max is 150 (vs eliminator's
              240.00). */}
          <FormSubtab
            type="silencer"
            cid={row.id}
            name="performance"
            label="Performance"
            open={sub('performance')}
            onToggle={api.toggleSubtab}
            sublistDataSeason={deriveSeasonValue(cms, section.season_id)}
          >
            <AirStreamFlow api={api} cms={cms} />
            <li>
              <div>
                <span className="unit">fpm</span>
                <MBareInput api={api} name="face_velocity_actual" readOnly extraClass="medium-input" />
                <MBareInput api={api} name="apd" readOnly={selectMode2} extraClass="medium-input" />
                <span className="label">
                  <span>Face Velocity</span> | <span>APD</span>
                </span>
              </div>
            </li>
            <li className="sublist-heading">
              <h3>
                <span>FREQUENCY</span>
                <span>DIL</span>
                <span>GN</span>
              </h3>
            </li>
            {octaves.map((hz) => (
              <li key={hz}>
                <div className="frequency">
                  <span className="unit">db</span>
                  <input
                    type="text"
                    data-formatter_type="integer"
                    autoComplete="off"
                    name={`dil_${hz}hz`}
                    className={`text-right medium-input model_field${selectMode2 ? ' non-clickable' : ''}`}
                    tabIndex={selectMode2 ? -1 : undefined}
                    readOnly={selectMode2}
                    value={String(model.get(`dil_${hz}hz`) ?? '')}
                    onChange={() => undefined}
                  />
                  <input
                    type="text"
                    data-formatter_type="integer"
                    autoComplete="off"
                    name={`gn_${hz}hz`}
                    className={`text-right medium-input model_field${selectMode2 ? ' non-clickable' : ''}`}
                    tabIndex={selectMode2 ? -1 : undefined}
                    readOnly={selectMode2}
                    value={String(model.get(`gn_${hz}hz`) ?? '')}
                    onChange={() => undefined}
                  />
                  <span className="label">{`@ ${hz === '31' ? '31.5' : hz} Hz`}</span>
                </div>
              </li>
            ))}
            <ComponentSeasonBlock
              api={api}
              section={section as Record<string, unknown>}
              cms={cms}
              component="Silencer"
              typePrefix="silencer"
              leavingMax={150}
            />
          </FormSubtab>

          <BlankOffSlot api={api} sectionId={section.id} />
        </ul>
      </form>
      {/* form.tmpl :519 — the note sits after </form>, as on eliminator/filter. */}
      <RequiredFieldNote />
    </>
  );
};

export default SilencerForm;
